import React, { createContext, useContext, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../Settings/appSettings';
import { useDataContext } from './useDataContext';
import { usePendingScoreQueue, EnqueueItem } from '../Hooks/usePendingScoreQueue';

interface PendingScoreContextType {
  enqueue: (items: EnqueueItem[]) => void;
  flushQueue: () => Promise<void>;
  pendingCount: number;
}

const PendingScoreContext = createContext<PendingScoreContextType | null>(null);

export const PendingScoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // CreateApi and useAuth are hooks — called here so refs always have the latest instances.
  // The refs are updated every render, giving the queue hook a stable reference that never
  // holds a stale token or auth state.
  const api = CreateApi();
  const auth = useAuth();
  const apiRef = useRef(api);
  const authRef = useRef(auth);
  apiRef.current = api;
  authRef.current = auth;

  const { scoreCode } = useDataContext();

  const queue = usePendingScoreQueue({ apiRef, authRef, scoreCode });

  return (
    <PendingScoreContext.Provider value={queue}>
      {children}
    </PendingScoreContext.Provider>
  );
};

export const usePendingScoreContext = (): PendingScoreContextType => {
  const context = useContext(PendingScoreContext);
  if (!context) {
    throw new Error('usePendingScoreContext must be used within a PendingScoreProvider');
  }
  return context;
};
